/**
 * Every word this console shows, in one file.
 *
 * The same rule the product console lives by, and the guards that enforce it
 * are copied alongside: no English in a component, keys written as literals at
 * the call site so the unused-message detector can see them, and no em dashes.
 *
 * The register differs from the product's, deliberately. The product speaks to
 * a seller about their money. This speaks to one operator about other people's
 * data, and its job is to make the weight of that visible rather than to
 * reassure.
 */
export const en = {
  app: {
    name: 'CosmOS Admin',
    tagline: 'Every tenant, and what can be done to them',
    /* Beside the name in the shell. Two consoles open at once is the norm. */
    marker: 'Admin',
  },

  signIn: {
    title: 'Sign in',
    body: 'This console can read and destroy any customer account. Access is limited to named administrators.',
    action: 'Sign in with your work account',
    working: 'Taking you to sign in',
    notConfiguredTitle: 'This platform has no admin surface',
    notConfiguredBody:
      'The platform answered, and it is not serving an admin API. It needs COSMOS_OIDC_ADMIN_AUDIENCE set to the admin app client id, or to be running in dev mode on a laptop.',
    devTitle: 'Development sign-in',
    devBody:
      'This platform has no identity provider, so pick an administrator. The roster still decides what they can do: this asserts who you are, not what you may.',
    devEmpty: 'The platform has no bootstrap administrator to sign in as.',
  },

  /*
    Five outcomes, not twelve, and each `t()` below is a literal at its call
    site in `SignIn.tsx`.

    Keys assembled from a variable are invisible to the unused-message
    detector, and a lookup table of them in the session module would move the
    words out of the view. The product console learned both of those and the
    shape here follows it.

    The collapse is by WHAT A PERSON SHOULD DO. `no-code`, `no-id-token`,
    `exchange-failed` and `provider-refused` are four faults in the provider and
    one situation for the person reading: it did not work, try again. Four
    sentences would be describing our plumbing rather than their problem.
  */
  signInError: {
    failed: 'Sign-in did not finish',
    failedBody: 'The identity provider did not complete the sign-in. Try again.',
    cancelled: 'Sign-in cancelled',
    cancelledBody: 'You stopped at the sign-in page. Nothing happened.',
    notAnAdmin: 'Not an administrator',
    notAnAdminBody:
      'That account signed in successfully, and it is not an administrator of this platform. Administrators are named individually here; being able to sign in to the product does not grant this console. Trying again will keep succeeding and keep landing here.',
    unverified: 'That sign-in did not start here',
    unverifiedBody:
      'The response did not match the request this tab made. Start again from this tab. If you did not begin a sign-in, tell someone before trying again.',
    unreachable: 'Nothing answered',
    unreachableBody:
      'The platform or the identity provider did not respond. Try again in a moment.',
    wrongIssuer: 'The identity provider is not the expected one',
    wrongIssuerBody:
      'The configuration came back naming a different service from the one this platform trusts. Do not sign in again until somebody has looked at this.',
  },

  nav: {
    flags: 'Feature flags',
    tenants: 'Accounts',
    admins: 'Administrators',
    audit: 'Audit log',
  },

  tenants: {
    title: 'Accounts',
    description: 'Every customer account on this platform. Metadata only: reading an account\u2019s books needs a token no administrator holds.',
    name: 'Account',
    plan: 'Plan',
    status: 'Status',
    members: 'People',
    events: 'Events',
    lastActivity: 'Last activity',
    noActivity: 'Nothing yet',
    search: 'Search accounts',
    noun: 'accounts',
    create: 'Create account',
    creating: 'Creating',
    createTitle: 'Create an account',
    createBody: 'This provisions the account, its chart of accounts and its owner. The owner is not emailed; they need to be told.',
    slug: 'Slug',
    slugHint: 'Lowercase letters, numbers and hyphens. It appears in URLs and it is what an operator types to confirm a purge, so it cannot contain spaces.',
    accountName: 'Account name',
    accountNameHint: 'Shown throughout the product. The customer sees this.',
    planHint: 'Decides which modules the account licenses.',
    ownerEmail: 'Owner email',
    ownerEmailHint: 'They sign in with this address. It must match what their identity provider asserts.',
    ownerName: 'Owner name',
    ownerNameHint: 'Optional. Defaults to the address.',
    createFailedTitle: 'The account was not created',
    createFailed: 'The platform did not create the account, and did not say why.',
    createdTitle: 'Account created',
    createdBody: '{name} is ready. Its owner has been invited but not emailed.',
    emptyTitle: 'No accounts yet',
    emptyBody: 'Nothing has been provisioned on this platform. Accounts appear here as soon as one is created.',
  },

  tenant: {
    missing: 'That account does not exist, or it has been removed.',
    aboutTitle: 'About this account',
    aboutBody: 'What the platform records about the account itself. None of it is the customer\u2019s own data.',
    plan: 'Plan',
    modules: 'Modules licensed',
    members: 'People with access',
    created: 'Created',
    lastActivity: 'Last activity',
    demoResettable: 'Demo account',
    demoYes: 'Yes, it can be reset to its seeded state',
    demoNo: 'No, this is a real account',
    demoHint: 'Only demo accounts can be reset. Real ones can only be purged, which is not reversible.',

    dataTitle: 'What is on this account',
    dataBody: 'Row counts by group, read from the platform now rather than from the list you clicked through.',
    dataGroup: 'Group',
    dataTables: 'Tables',
    dataRows: 'Rows',
    dataTotal: '{rows} rows in total, including {events} events.',
    dataEmptyTitle: 'This account holds nothing',
    dataEmptyBody: 'Nothing has been ingested or recorded here. There is nothing for a purge to remove.',

    unclassifiedTitle: 'Some tables are not classified',
    unclassifiedBody:
      'These carry customer data and belong to no group, so nobody can say what a purge would do to them: {tables}. Whoever added them needs to classify them in the purge service.',

    resetAction: 'Reset to seeded state',
    resetting: 'Resetting',
    resetTitle: 'Reset this demo account',
    resetBody: 'Puts the account back to how it was seeded: the chart of accounts is rebuilt, and everything ingested since is removed.',
    /*
      REASONS, not a disabled button. Each says which of the two things is
      true, because "not available" leaves an operator guessing whether the
      control is missing because of the account or because of them.
    */
    resetNotDemo: 'This is a real account, so it cannot be reset. Only accounts explicitly marked as demos can be, and the platform refuses this one however it is asked. Emptying a real account is a purge, which is not reversible.',
    resetNoRole: 'Resetting an account needs the admin role.',
    resetReady: 'This is a demo account with {events} events. Resetting rebuilds its chart of accounts and removes everything ingested since.',
    resetEmpty: 'This is a demo account and nothing has been ingested into it, so a reset would rebuild its chart of accounts and remove nothing.',
    resetDoneTitle: 'Account reset',
    resetDoneBody: '{rows} rows removed from {name}, and its seeded state rebuilt.',
    resetFailedTitle: 'The reset did not run',
    purgeAction: 'Purge this account',
    purgedTitle: 'Account purged',
    purgedBody: '{rows} rows removed from {slug}, recorded against {actor}. Access was not changed.',
    purgeRefusedTitle: 'The purge did not run',
    purgeFailedUnknown: 'The platform did not complete the purge, and did not say why.',
  },

  purge: {
    title: 'Purge {name}',
    description: 'This removes the account\u2019s data. It cannot be undone.',
    warningTitle: 'This cannot be undone',
    warningBody:
      '{rows} rows will be deleted from {name}, including {events} events. There is no backup and no undo: the event log is the record, and this removes it.',
    keeps:
      'Access is not affected. The account, the people on it and their roles all survive, so anybody who can sign in today can still sign in afterwards, to an empty account.',
    confirmLabel: 'Type {slug} to confirm',
    confirmHint: 'The platform checks this again against the account it holds, so a stale page cannot confirm its way past it.',
    confirm: 'Purge the account',
    working: 'Purging',
  },

  admins: {
    title: 'Administrators',
    description: 'Who can reach this console, and what each of them may do. Everyone here can read this list; only a super_admin can change it.',
    person: 'Person',
    role: 'Role',
    status: 'Status',
    bootstrap: 'Bootstrap',
    lastSignIn: 'Last signed in',
    neverSignedIn: 'Never',
    added: 'Added',
    actions: 'Actions',
    search: 'Search administrators',
    noun: 'administrators',
    suspend: 'Suspend',
    reactivate: 'Reactivate',
    add: 'Add administrator',
    adding: 'Adding',
    addTitle: 'Add an administrator',
    addBody: 'They will be able to sign in with their work account. Nothing is emailed: they need to be told.',
    email: 'Email address',
    emailHint: 'Must match the address their identity provider asserts, or they will be refused.',
    name: 'Name',
    nameHint: 'Shown in this console. Optional.',
    roleLabel: 'Role',
    roleSupport: 'Reads accounts, their row counts and the audit trail. Changes nothing.',
    roleAdmin: 'The above, and can destroy an account\u2019s data.',
    roleSuper: 'The above, and can add, suspend and revoke administrators.',
    changeRefusedTitle: 'That change was refused',
    changeFailed: 'The platform did not accept the change, and did not say why.',
    emptyTitle: 'The roster came back empty',
    emptyBody: 'There is always at least one administrator, so this means the list could not be read rather than that nobody is on it.',
  },

  flags: {
    // ── The account section ──
    accountTitle: 'Features',
    accountBody: 'What this account can see. A choice here beats the rollout, either way, and affects no other account.',
    none: 'No features are behind a flag right now.',
    followsRollout: 'Follows the rollout. Currently {stage}.',
    forcedOn: 'Switched on for this account, whatever the rollout says',
    forcedOff: 'Switched off for this account, whatever the rollout says',
    on: 'On',
    off: 'Off',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    clear: 'Follow the rollout',
    saveFailed: 'That change did not save',
    elsewhereTitle: 'Managed outside this console',
    elsewhereBody: 'Flags come from {provider}. Change them there and this account will follow.',

    // ── The rollout page ──
    title: 'Feature flags',
    body: 'What each feature is ready for, and who can see it.',
    provider: 'Backend',
    reachEveryone: 'Everyone',
    reachAccounts: '{count} account(s)',
    reachNobody: 'Nobody yet',
    reachForcedOn: '+{count} switched on',
    reachForcedOff: '−{count} switched off',
    stageLabel: 'Move to',
    retreatWarning: 'Moving back to {stage} takes this feature away from accounts that have it now.',
    retreatConfirm: 'Move it back anyway',
    retreatCancel: 'Leave it where it is',
    internalTitle: 'Who sees it at internal',
    internalBody: 'Cosmos staff, by person. Gated on the individual rather than the account, because staff work inside customer accounts.',
    internalNone: 'Nobody. The feature is invisible at this stage.',
    addPerson: 'Add',
    removePerson: 'Remove',
    stage: {
      off: 'Off',
      internal: 'Internal only',
      alpha: 'Alpha',
      beta: 'Beta',
      general: 'Generally available',
    },
  },
  audit: {
    title: 'Audit log',
    description: 'Every administrative action, recorded as it happened. Nothing can edit or delete an entry, including the administrator who made it.',
    when: 'When',
    actor: 'Administrator',
    action: 'Action',
    subject: 'Account',
    noSubject: 'No account',
    status: 'Outcome',
    noun: 'entries',
    total: '{total} entries recorded.',
    emptyTitle: 'Nothing recorded yet',
    emptyBody: 'No administrator has done anything on this platform. The first sign-in writes an entry here.',
  },

  session: {
    signOut: 'Sign out',
  },


  /*
    Copied verbatim from the product console's dictionary, because these are
    the words the copied components ask for by key. Changing one here and not
    there is how two consoles come to disagree about what "Clear" clears.
  */
  a11y: {
    close: 'Close',
    closeNavigation: 'Close navigation',
    mainNavigation: 'Main',
    pagination: 'Pagination',
  },

  shell: {
    couldNotLoad: 'Could not load this',
    loading: 'Loading',
  },

  table: {
    clearFacet: 'Clear {facet}',
    clearFilters: 'Clear filters',
    filteredEmptyBody: {
      one: 'The one row loaded is excluded by the current search and filters.',
      other: 'All {total} rows are still loaded. The current search and filters exclude every one.',
    },
    filteredEmptyTitle: 'Nothing matches these filters',
    next: 'Next',
    nothingToFilter: 'Nothing to filter by',
    nothingToShow: 'Nothing to show',
    pageOf: 'Page {page} of {pages}',
    previous: 'Previous',
    retry: 'Retry',
    searchLabel: 'Search this table',
    sortAscending: 'Sorted low to high. Select to reverse.',
    sortDescending: 'Sorted high to low. Select to clear.',
    sortNone: 'Select to sort',
  },

  common: {
    loading: 'Loading',
    clear: 'Clear',
    cancel: 'Cancel',
    of: '{shown} of {total}',
    /* An em dash, the house convention for "there is no value here". */
    noValue: '\u2014',
    none: 'None',
  },
} as const;
